import {
    createProtocolStepEvent,
    deleteProtocolStepEvent,
    updateProtocol,
    updateProtocolStep,
} from '@/protocols/services/protocols'
import { ApiError, PendingWrite } from './types'
import { useWriteStatusStore } from './useWriteStatusStore'
import { convertPayloadDatesToDayjs } from './dates'
import {WriteStore} from './writeStore'

const TICK_INTERVAL_MS = 1000
const SEND_CONCURRENCY = 8
const CLAIM_BATCH_LIMIT = 100
const BACKOFF_LOOKBACK_MS = 1000
// Defense-in-depth: with request timeouts a tick should never run this
// long, but if some future code path awaits something that never
// settles, the watchdog abandons the stuck tick instead of letting it
// wedge the drainer forever (see the epoch notes below).
const TICK_WATCHDOG_MS = 60_000

function isRetryable(err: ApiError): boolean {
    return (
        err.status === undefined ||
        err.status >= 500 ||
        err.status === 408 ||
        err.status === 429 ||
        err.status === 401 ||
        // 403 (Forbidden) is treated as transient because the user
        // might gain the permission later (e.g. an admin grants them
        // access on the server, then they retry). Marking dead would
        // lose clinical-trial data the user could legitimately retry.
        err.status === 403
    )
}

export class ProtocolWriteDrainer {
    private intervalId: number | null = null
    private inFlight = false
    private tickStartedAt = 0
    // Monotonic generation counter. Bumped when the watchdog abandons a
    // stuck tick; every tick captures the epoch at start and discards its
    // results (markPersisted/markFailed/store notifications) if the epoch
    // has moved on by the time its dispatches settle. Without this, an
    // abandoned tick that wakes up later could corrupt the outcome of rows
    // a newer tick has already re-claimed and re-dispatched.
    private epoch = 0
    private abortControllers = new Map<number, AbortController>()

    constructor(
        private companyId: string,
        private protocolId: string,
        private store: WriteStore,
    ) {}

    start(): void {
        void this.tickSafe()
        this.intervalId = window.setInterval(() => {
            if (this.inFlight) {
                // Watchdog: a tick still running after the cap is treated
                // as abandoned. Bump the epoch so its late results are
                // discarded, abort in-flight HTTP so a late PUT cannot
                // overwrite a later skip, clear the guard, and fall
                // through to start a fresh tick.
                if (Date.now() - this.tickStartedAt < TICK_WATCHDOG_MS) return
                this.epoch += 1
                this.abortInFlight()
                this.inFlight = false
                console.warn(
                    `[protocolWriteDrainer] tick exceeded ${TICK_WATCHDOG_MS}ms; abandoning it (epoch ${this.epoch})`,
                )
            }
            void this.tickSafe()
        }, TICK_INTERVAL_MS)
    }

    stop(): void {
        if (this.intervalId !== null) {
            window.clearInterval(this.intervalId)
            this.intervalId = null
        }
        this.abortInFlight()
    }

    private abortInFlight(): void {
        for (const ac of this.abortControllers.values()) ac.abort()
        this.abortControllers.clear()
    }

    private async tickSafe(): Promise<void> {
        if (this.inFlight) return
        this.inFlight = true
        this.tickStartedAt = Date.now()
        const myEpoch = this.epoch
        try {
            await this.tick(myEpoch)
        } catch (e) {
            console.error('[protocolWriteDrainer] tick failed', e)
        } finally {
            // Only release the guard if this tick wasn't abandoned; if it
            // was, the watchdog already bumped the epoch and started (or
            // will start) a fresh tick whose inFlight flag must survive.
            if (this.epoch === myEpoch) {
                this.inFlight = false
            }
        }
    }

    private async tick(epoch: number = this.epoch): Promise<void> {
        // Drain-until-empty: claim, dispatch, re-claim, re-dispatch...
        // until the store reports no claimable rows. Chains (e.g.,
        // create+delete for the same coalesce_key) advance naturally
        // across iterations within a single tick:
        //
        //   - Successful dispatch transitions the row to 'persisted';
        //     the next iteration's claim sees the next-in-line row
        //     for that coalesce_key as the earliest pending.
        //   - Non-retryable failure transitions to 'dead'; same
        //     advance behavior.
        //   - Retryable failure keeps the row at 'pending' with a
        //     recent last_attempt_at; the cutoff filter excludes it
        //     (and all later rows for the same coalesce_key, since
        //     those are gated by the still-pending earlier row). The
        //     chain stalls until the backoff window elapses — at that
        //     point the next tick (or this tick, after other work
        //     clears) picks it up.
        //
        // This maximizes throughput when the server is up: a single
        // tick flushes as much as possible, bounded only by the
        // concurrency cap and dispatch latency.
        //
        // WARNING: We run claimed writes concurrently, so it's important that
        //          we never claim more than 1 per coalesce_key as we need to
        //          guarantee in order within each coalesce_key. However,
        //          we don't **currently** need to guarantee in order execution
        //          across different coalesce keys. Coalesce keys are assigned
        //          to equal the id of the object being updated, guaranteeing
        //          in order updates of the same object.
        for (;;) {
            // Abandoned by the watchdog: stop claiming further work; any
            // already-dispatched results are discarded in sendOne.
            if (epoch !== this.epoch) return
            const cutoff = Date.now() - BACKOFF_LOOKBACK_MS
            const claimed = await this.store.claim({
                companyId: this.companyId,
                protocolId: this.protocolId,
                backoffCutoffMs: cutoff,
                limit: CLAIM_BATCH_LIMIT,
            })
            if (claimed.length === 0) return

            for (let i = 0; i < claimed.length; i += SEND_CONCURRENCY) {
                const batch = claimed.slice(i, i + SEND_CONCURRENCY)
                await Promise.allSettled(batch.map((w) => this.sendOne(w, epoch)))
            }
        }
    }

    private async sendOne(write: PendingWrite, epoch: number): Promise<void> {
        const writeKey = write.coalesceKey ?? write.stepId
        const ac = new AbortController()
        this.abortControllers.set(write.id, ac)

        try {
            const response = await this.dispatch(write, ac.signal)
            // Abandoned while in flight: a newer tick may have re-claimed
            // and re-dispatched this row, so discard the stale outcome.
            if (epoch !== this.epoch) return
            await this.store.markPersisted(this.companyId, this.protocolId, write.id)
            if (writeKey) useWriteStatusStore.getState().markPersisted(writeKey)
            // Notify subscribers of the persist. The drainer fires this for
            // any non-null response, including boolean returns from
            // event_delete. Subscribers branch on writeType — for
            // event_delete, ProtocolRecorder removes the entry from its
            // local event list (so post-refresh deletes are reflected in
            // the UI without another refresh).
            try {
                if (response !== undefined && response !== null) {
                    await this.store.notifyWritePersisted(
                        this.companyId, this.protocolId, write.id, write.coalesceKey, write.writeType, response,
                    )
                }
            } catch (err) {
                 // don't allow failure here to retry the pending write.
                console.error(err)
            }
        } catch (err) {
            // Abandoned while in flight — same discard rule as above.
            if (epoch !== this.epoch) return
            const apiErr = err as ApiError
            const retryable = isRetryable(apiErr)
            await this.store.markFailed(this.companyId, this.protocolId, write.id, apiErr.message ?? 'unknown error', retryable)
            if (writeKey) {
                if (retryable) {
                    useWriteStatusStore.getState().markPending(writeKey, { incrementAttemptCount: true })
                } else {
                    useWriteStatusStore.getState().markDead(write, apiErr.message ?? 'unknown error')
                }
            }
        } finally {
            this.abortControllers.delete(write.id)
        }
    }

    /**
     * CRITICAL:
     *  It's critical that all failures in these API calls to the server return a failed promise
     * if the api call does not succeed so we can mark the pending write as failed/pending for retry.
     * One case to keep in mind is redirect on auth failures, be sure you return failed promise.
     * @param write
     * @private
     */
    private async dispatch(write: PendingWrite, signal?: AbortSignal): Promise<unknown> {
        const { writeType, payload } = write
        const silentConfig = { silent: true, signal } as const
        switch (writeType) {
            case 'step_update':
                return updateProtocolStep(
                    this.companyId as any,
                    convertPayloadDatesToDayjs(payload) as any,
                    silentConfig,
                )
            case 'protocol_update':
                return updateProtocol(this.companyId as any, payload as any, silentConfig)
            case 'event_create':
                return this.dispatchEventCreate(payload as any, silentConfig)
            case 'event_delete':
                return this.dispatchEventDelete(payload as any, silentConfig)
            default: {
                const exhaustive: never = writeType
                throw new Error(`Unknown write type: ${exhaustive}`)
            }
        }
    }

    private async dispatchEventCreate(event: { id?: string; protocolStepId?: string; event: any }, config?: { silent?: boolean }): Promise<unknown> {
        return createProtocolStepEvent({
            companyId: this.companyId as any,
            protocolId: this.protocolId as any,
            stepId: event.protocolStepId,
            dto: {
                // Pass the client's canonical id so the server stores the
                // same id we used locally; the delete path then uses the
                // same id without any tempId → realId translation.
                id: event.id ?? '',
                event: {
                    // Forward the canonical inner EventId as well so retries
                    // re-use the same {schema}.events row instead of letting
                    // the backend generate a fresh id per replay.
                    id: event.event.id,
                    subject_id: event.event.subjectId,
                    device_id: event.event.deviceId,
                    event_type: event.event.eventType,
                    metadata: event.event.metadata ?? {},
                    tags: event.event.tags ?? [],
                    start_time: event.event.startTime,
                    end_time: event.event.endTime,
                    step_id: event.protocolStepId,
                }
            },
            config: config as any,
        })
    }

    private async dispatchEventDelete(payload: { id: string }, config?: {silent?: boolean; signal?: AbortSignal}): Promise<unknown> {
        return deleteProtocolStepEvent({
            companyId: this.companyId as any,
            protocolId: this.protocolId as any,
            id: payload.id as any,
        }, config)
    }
}
